import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '@/components/SEO/SEOHead'
import { buildGraph, breadcrumbListData, serviceSchema, faqData } from '@/data/structured-data'
import PageHeader from '@/components/Common/PageHeader'
import { contactInfo } from '@/data/contact'
import { toTelHref } from '@/utils/phone'

const KisirlastirmaPage: React.FC = () => {
  const telHref = toTelHref(contactInfo.phone)

  return (
    <>
      <SEOHead
        title="Kısırlaştırma | Kedi ve Köpek"
        description="Şanlıurfa Karaköprü'de kedi ve köpek kısırlaştırma. Uygun yaş, operasyon süreci, iyileşme ve fiyatı etkileyen faktörler. Randevu için arayın."
        keywords="kedi kısırlaştırma ücreti 2024, köpek kısırlaştırma fiyatları, dişi köpeklerde kısırlaştırma, erkek köpek kısırlaştırma fiyat, kedi kısırlaştırma ne kadar"
        canonicalUrl="/kisirlastirma"
        structuredData={buildGraph(
          serviceSchema({
            name: 'Kedi ve Köpek Kısırlaştırma',
            description: 'Güvenli anestezi, ağrı yönetimi ve steril ameliyathane ile kedi-köpek kısırlaştırma operasyonları.',
            url: '/kisirlastirma',
            serviceType: 'Kısırlaştırma operasyonu'
          }),
          faqData([
            {
              question: 'Kedi kısırlaştırma ücreti ne kadar?',
              answer: 'Ücret; cinsiyet, yaş, kilo ve anestezi riskine göre değişir. Ön muayene sonrası net fiyat bilgisi paylaşılır. Güncel fiyat için bizi arayabilirsiniz.'
            },
            {
              question: 'Kısırlaştırma için uygun yaş nedir?',
              answer: 'Genellikle 6-8 aylık dönem tercih edilir. Ancak ırk, gelişim durumu ve sağlık geçmişine göre bu aralık değişebilir; muayenede birlikte karar veriyoruz.'
            },
            {
              question: 'Operasyon sonrası iyileşme ne kadar sürer?',
              answer: 'Çoğu hasta 24-48 saat içinde normal aktivitesine döner. Dikişler genellikle 10-14 gün içinde kontrol edilir. Bu süreçte koruyucu yaka kullanımı önerilir.'
            }
          ]),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Kısırlaştırma', url: '/kisirlastirma' }
          ])
        )}
      />
      <PageHeader
        title="Kısırlaştırma"
        subtitle="Kedi ve köpeklerde kısırlaştırma: uygun yaş, operasyon süreci ve iyileşme."
      />

      <section className="section-padding">
        <div className="container-custom">
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mb-10">
            Kısırlaştırma, üreme kontrolünün yanında sağlık açısından da belirgin fayda sağlayan bir
            operasyondur. Uygun yaş, hazırlık ve iyileşme süreci kedi ile köpekte farklılık gösterdiği
            için ayrıntıları tür bazında ele aldık. Aşağıdan hayvanınıza uygun sayfayı seçin.
          </p>

          {/* Tür seçimi — asıl içerik tür sayfalarında */}
          <div className="grid md:grid-cols-2 gap-6 mb-14">
            {[
              {
                to: '/kedi/kisirlastirma',
                image: '/persian.webp',
                title: 'Kedi Kısırlaştırma',
                desc: 'Genellikle 6–8 aylık dönemde, ilk kızgınlık öncesinde uygulanır. Operasyon aynı gün tamamlanır.'
              },
              {
                to: '/kopek/kisirlastirma',
                image: '/golden-retriever.webp',
                title: 'Köpek Kısırlaştırma',
                desc: 'Uygun yaş ırka göre değişir: küçük ırklarda 6–9 ay, büyük ırklarda iskelet gelişimi sonrası.'
              }
            ].map(c => (
              <Link
                key={c.to}
                to={c.to}
                className="group rounded-2xl overflow-hidden border border-gray-200 hover:border-primary-300 hover:shadow-xl transition-all bg-white"
              >
                <img src={c.image} alt={c.title} loading="lazy" className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 group-hover:text-primary-700 mb-2">{c.title}</h2>
                  <p className="text-gray-600 leading-relaxed mb-3">{c.desc}</p>
                  <span className="text-sm font-semibold text-primary-600">Ayrıntıları oku →</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-3">Her iki türde de ortak olanlar</h2>
                <ul className="space-y-2">
                  {[
                    'Operasyon öncesi genel muayene ve gerektiğinde kan tahlili yapılır.',
                    'Anestezi dozu kiloya göre hesaplanır, operasyon boyunca hasta takip edilir.',
                    'Ağrı yönetimi operasyon sırasında ve sonrasında planlanır.',
                    'Taburcu genellikle aynı gün gerçekleşir.',
                    'Koruyucu yaka dikişler kapanana kadar kullanılır.',
                    'Dikiş kontrolü 10–14 gün içinde yapılır.'
                  ].map(t => (
                    <li key={t} className="flex items-start gap-2 text-gray-700 leading-relaxed">
                      <span className="text-primary-500 mt-1">•</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-3">Fiyat neye göre belirlenir?</h2>
                <p className="text-gray-700 leading-relaxed">
                  Kısırlaştırma ücreti sabit değildir. Tür, cinsiyet, kilo, yaş, anestezi riski ve
                  gerekli ön tahliller fiyatı doğrudan etkiler. Dişilerde operasyon daha kapsamlı
                  olduğu için erkeklerden farklıdır. Bu nedenle internette gördüğünüz tek bir rakam
                  yanıltıcı olur; muayene sonrası size net tutarı söylüyoruz. Telefonda da tahmini
                  bir aralık verebiliriz.
                </p>
              </div>
            </div>

            <aside className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-2">Randevu ve fiyat</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Hayvanınızın türünü, yaşını ve kilosunu söyleyin; uygun tarihi ve tahmini tutarı paylaşalım.
                </p>
                <a href={telHref} className="btn-primary w-full inline-flex items-center justify-center">
                  {contactInfo.phone}
                </a>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-3">İlgili sayfalar</h3>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/hizmetler/cerrahi" className="text-primary-700 hover:underline">Cerrahi hizmetler</Link></li>
                  <li><Link to="/klinik/ameliyathane" className="text-primary-700 hover:underline">Ameliyathanemiz</Link></li>
                  <li><Link to="/kedi/asi-takvimi" className="text-primary-700 hover:underline">Kedi aşı takvimi</Link></li>
                  <li><Link to="/kopek/asi-takvimi" className="text-primary-700 hover:underline">Köpek aşı takvimi</Link></li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  )
}

export default KisirlastirmaPage


